// Email service for booking confirmations
import emailjs from '@emailjs/browser';

// Initialize EmailJS (you'll need to set up EmailJS account)
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'your_service_id';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'your_template_id';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key';

console.log('📧 EmailJS Service Configuration:');
console.log('- Service ID:', EMAILJS_SERVICE_ID);
console.log('- Template ID:', EMAILJS_TEMPLATE_ID);
console.log('- Public Key:', EMAILJS_PUBLIC_KEY ? 'Set' : 'Missing');
console.log('- EmailJS object:', emailjs);

// Initialize EmailJS with error handling
try {
  if (emailjs && typeof emailjs.init === 'function') {
    emailjs.init(EMAILJS_PUBLIC_KEY);
    console.log('✅ EmailJS initialized successfully');
  } else {
    console.error('❌ EmailJS object or init method not available');
  }
} catch (error) {
  console.error('❌ EmailJS initialization failed:', error);
}

// Send booking confirmation email
export async function sendBookingConfirmationEmail(bookingDetails) {
  try {
    console.log('📧 EmailJS Configuration Check:');
    console.log('Service ID:', EMAILJS_SERVICE_ID);
    console.log('Template ID:', EMAILJS_TEMPLATE_ID);
    console.log('Public Key:', EMAILJS_PUBLIC_KEY ? 'Set' : 'Missing');
    
    // Validate required fields
    if (!bookingDetails.guestEmail) {
      console.error('❌ Guest email is missing, using business email');
      bookingDetails.guestEmail = 'booking@skylinebackpackers.in';
    }
    
    if (EMAILJS_SERVICE_ID === 'your_service_id' || EMAILJS_TEMPLATE_ID === 'your_template_id') {
      throw new Error('EmailJS not properly configured - using placeholder values');
    }
    
    // Additional validation for empty email strings
    if (!bookingDetails.guestEmail.trim() || bookingDetails.guestEmail === '') {
      console.error('❌ Guest email is empty, using business email');
      bookingDetails.guestEmail = 'booking@skylinebackpackers.in';
    }
    
    // Parse booking data properly
    const bookingData = bookingDetails.bookingData || {};
    const selectedPackage = bookingDetails.selectedPackage || bookingData.package || {};
    const selectedBeds = bookingDetails.selectedBeds || bookingData.selectedBeds || [];
    
    // Calculate values with proper fallbacks
    const nights = bookingDetails.nights || bookingData.nights || 1;
    const beds = bookingDetails.beds || bookingData.beds || selectedBeds.length || 1;
    const packagePrice = selectedPackage.price || bookingData.package?.price || 599;
    
    // Calculate subtotal based on package type
    let subtotal;
    const isHourlyPlan = selectedPackage.plan?.includes('Hourly') || false;
    const isWeeklyPlan = selectedPackage.plan?.includes('Weekly') || false;
    const isMonthlyPlan = selectedPackage.plan?.includes('Monthly') || false;
    
    if (isHourlyPlan || isWeeklyPlan || isMonthlyPlan) {
      subtotal = packagePrice * beds;
    } else {
      subtotal = packagePrice * beds * nights;
    }
    
    const convenienceFee = Math.round(subtotal * 0.05);
    const totalAmount = bookingDetails.total || bookingDetails.amount || bookingData.total || (subtotal + convenienceFee);
    
    // Format dates
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    };
    
    // Format bed numbers for display and pass raw bed data
    const bedNumbersDisplay = selectedBeds.length > 0 ? selectedBeds.map(bed => {
      if (typeof bed === 'string') return bed;
      if (typeof bed === 'object') return bed.id || bed.number || bed.label || bed.name;
      return bed.toString();
    }).join(', ') : `${beds} bed${beds > 1 ? 's' : ''}`;
    
    // Raw bed data for Invoice component
    const rawBedNumbers = selectedBeds.length > 0 ? selectedBeds.map(bed => {
      if (typeof bed === 'string') return bed;
      if (typeof bed === 'object') return bed.id || bed.number || bed.label || bed.name || bed;
      return bed;
    }) : [];
    
    // Service description based on package type
    const getServiceDescription = () => {
      if (selectedPackage.plan === 'Hourly Plan') {
        return `${selectedPackage.plan} - ${selectedPackage.duration}`;
      } else if (selectedPackage.plan === 'Weekly Plan') {
        return `${selectedPackage.plan} - ${selectedPackage.duration}`;
      } else {
        return `${selectedPackage.plan || 'Regular Plan'} - ${selectedPackage.duration || '12-24 Hours'}`;
      }
    };
    
    // Quantity description based on package type
    const getQuantityDescription = () => {
      if (selectedPackage.plan === 'Hourly Plan') {
        return `${beds} bed${beds > 1 ? 's' : ''}`;
      } else if (selectedPackage.plan === 'Weekly Plan') {
        return `${beds} bed${beds > 1 ? 's' : ''}`;
      } else {
        return `${beds} bed${beds > 1 ? 's' : ''} × ${nights} night${nights > 1 ? 's' : ''}`;
      }
    };

    // Create structured booking details for Invoice component
    const invoiceBookingDetails = {
      bookingName: bookingDetails.guestName,
      guestName: bookingDetails.guestName,
      bookingId: bookingDetails.id,
      checkInDate: formatDate(bookingDetails.checkIn || bookingData.start),
      checkOutDate: formatDate(bookingDetails.checkOut || bookingData.end),
      beds: beds,
      nights: nights,
      bedNumbers: rawBedNumbers, // Array format for Invoice component
      ratePerNight: packagePrice,
      taxRate: 0, // Using convenience fee instead
      extraCharges: [],
      checkInTime: bookingDetails.checkInTime,
      checkOutTime: bookingDetails.checkOutTime,
      packageInfo: {
        plan: selectedPackage.plan || 'Regular Plan',
        duration: selectedPackage.duration || '12-24 Hours',
        price: packagePrice
      },
      totalAmount: totalAmount,
      guestDetails: {
        name: bookingDetails.guestName,
        email: bookingDetails.guestEmail,
        phone: bookingDetails.guestPhone
      },
      selectedBeds: rawBedNumbers,
      bookingData: {
        selectedBeds: rawBedNumbers
      }
    };

    // Using individual parameters that match EmailJS template variables
    const emailParams = {
      name: bookingDetails.guestName,
      email: bookingDetails.guestEmail,
      subject: `Booking Confirmation - ${bookingDetails.id}`,
      message: `Dear ${bookingDetails.guestName}, Thank you for choosing Skyline Backpackers! Your booking has been confirmed.`,
      
      // All required template variables
      booking_id: bookingDetails.id,
      guests: beds.toString(),
      booking_date: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
      check_in: formatDate(bookingDetails.checkIn || bookingData.start),
      check_out: formatDate(bookingDetails.checkOut || bookingData.end),
      guest_name: bookingDetails.guestName,
      guest_email: bookingDetails.guestEmail,
      guest_phone: bookingDetails.guestPhone || 'Not provided',
      package_plan: selectedPackage.plan || 'Regular Plan',
      package_duration: selectedPackage.duration || '12-24 Hours',
      package_price: packagePrice.toString(),
      service_description: getServiceDescription(),
      rate_per_bed: packagePrice.toString(),
      quantity_description: getQuantityDescription(),
      bed_subtotal: subtotal.toString(),
      bed_numbers: bedNumbersDisplay,
      bed_numbers_raw: JSON.stringify(rawBedNumbers), // For template use
      subtotal: subtotal.toString(),
      convenience_fee: convenienceFee.toString(),
      total_amount: totalAmount.toString(),
      
      // Additional fields
      from_name: 'Skyline Backpackers',
      reply_to: 'booking@skylinebackpackers.in',
      
      // Invoice data for potential HTML generation
      invoice_data: JSON.stringify(invoiceBookingDetails)
    };

    console.log('📧 Email Calculation Debug Info:');
    console.log('- Package Price:', packagePrice);
    console.log('- Beds:', beds);
    console.log('- Nights:', nights);
    console.log('- Subtotal:', subtotal);
    console.log('- Convenience Fee:', convenienceFee);
    console.log('- Total Amount:', totalAmount);
    console.log('- Selected Package:', selectedPackage);
    console.log('- Booking Data Package:', bookingData.package);
    console.log('📧 Bed Details Debug Info:');
    console.log('- Selected Beds (raw):', selectedBeds);
    console.log('- Bed Numbers Display:', bedNumbersDisplay);
    console.log('- Raw Bed Numbers Array:', rawBedNumbers);
    console.log('- Invoice Booking Details:', invoiceBookingDetails);
    console.log('📧 Sending email with params:', emailParams);

    // Re-initialize EmailJS before sending (ensures proper setup)
    emailjs.init(EMAILJS_PUBLIC_KEY);

    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      emailParams
    );

    console.log('✅ Booking confirmation email sent successfully:', response);
    return {
      success: true,
      messageId: response.text,
      message: 'Booking confirmation email sent successfully'
    };

  } catch (error) {
    console.error('❌ Failed to send booking confirmation email:', error);
    console.error('❌ Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    return {
      success: false,
      error: error.message || 'Failed to send email'
    };
  }
}

// Send admin notification email
export async function sendAdminNotificationEmail(bookingDetails) {
  try {
    const emailParams = {
      to_email: 'admin@skylinebackpackers.in', // Admin email
      to_name: 'Admin',
      from_name: 'Skyline Backpackers System',
      from_email: 'booking@skylinebackpackers.in',
      reply_to: 'booking@skylinebackpackers.in',
      subject: `New Booking Received - ${bookingDetails.id}`,
      booking_id: bookingDetails.id,
      guest_name: bookingDetails.guestName,
      guest_email: bookingDetails.guestEmail,
      guest_phone: bookingDetails.guestPhone,
      check_in: bookingDetails.checkIn,
      check_out: bookingDetails.checkOut,
      nights: bookingDetails.nights,
      beds: bookingDetails.beds,
      total_amount: bookingDetails.total || bookingDetails.amount,
      payment_status: 'Paid',
      created_at: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
    };

    // You can create a separate template for admin notifications
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      'admin_notification_template', // Different template for admin
      emailParams
    );

    console.log('✅ Admin notification email sent successfully:', response);
    return {
      success: true,
      messageId: response.text
    };

  } catch (error) {
    console.error('❌ Failed to send admin notification email:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

// Fallback email service using a simple API (if EmailJS fails)
export async function sendEmailFallback(bookingDetails) {
  try {
    // This is a placeholder for a backend email service
    // You can implement this with your preferred email service
    const emailData = {
      to: bookingDetails.guestEmail,
      from: 'booking@skylinebackpackers.in',
      subject: `Booking Confirmation - ${bookingDetails.id}`,
      html: generateBookingEmailHTML(bookingDetails)
    };

    // Replace this with your actual email API endpoint
    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailData)
    });

    if (response.ok) {
      return { success: true, message: 'Email sent via fallback service' };
    } else {
      throw new Error('Fallback email service failed');
    }

  } catch (error) {
    console.error('❌ Fallback email service failed:', error);
    return { success: false, error: error.message };
  }
}

// Generate HTML email template
function generateBookingEmailHTML(bookingDetails) {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: #3399cc; color: white; padding: 20px; text-align: center; }
        .content { padding: 20px; background: #f9f9f9; }
        .booking-details { background: white; padding: 15px; margin: 10px 0; border-radius: 5px; }
        .footer { background: #333; color: white; padding: 15px; text-align: center; }
        .highlight { color: #3399cc; font-weight: bold; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>🏠 Skyline Backpackers</h1>
          <h2>Booking Confirmation</h2>
        </div>
        
        <div class="content">
          <p>Dear <strong>${bookingDetails.guestName}</strong>,</p>
          
          <p>Thank you for choosing Skyline Backpackers! Your booking has been confirmed.</p>
          
          <div class="booking-details">
            <h3>📋 Booking Details</h3>
            <p><strong>Booking ID:</strong> <span class="highlight">${bookingDetails.id}</span></p>
            <p><strong>Check-in:</strong> ${bookingDetails.checkIn}</p>
            <p><strong>Check-out:</strong> ${bookingDetails.checkOut}</p>
            <p><strong>Duration:</strong> ${bookingDetails.nights} nights</p>
            <p><strong>Beds:</strong> ${bookingDetails.beds}</p>
            <p><strong>Total Amount:</strong> ₹${bookingDetails.total || bookingDetails.amount}</p>
            <p><strong>Payment Status:</strong> ✅ Paid</p>
          </div>
          
          <div class="booking-details">
            <h3>📍 Location Details</h3>
            <p><strong>Address:</strong> Sindhi Colony, Hyderabad</p>
            <p><strong>Contact:</strong> +91 90327 97797</p>
            <p><strong>Email:</strong> booking@skylinebackpackers.in</p>
          </div>
          
          <div class="booking-details">
            <h3>📝 Important Information</h3>
            <ul>
              <li>Please carry a valid ID proof during check-in</li>
              <li>Check-in time: 12:00 PM onwards</li>
              <li>Check-out time: 11:00 AM</li>
              <li>Free cancellation up to 24 hours before check-in</li>
            </ul>
          </div>
        </div>
        
        <div class="footer">
          <p>Thank you for choosing Skyline Backpackers!</p>
          <p>📧 booking@skylinebackpackers.in | 📞 +91 90327 97797</p>
        </div>
      </div>
    </body>
    </html>
  `;
}
